import { LOCALE_ID, type Provider } from '@angular/core';

export const SUPPORTED_BROWSER_LOCALES = ['en-US', 'en-GB', 'de-DE', 'nl-NL', 'tr-TR'] as const;

export type SupportedBrowserLocale = (typeof SUPPORTED_BROWSER_LOCALES)[number];

export const EMEA_BROWSER_LOCALES = [
    'en-GB',
    'de-DE',
    'nl-NL',
    'tr-TR'
] as const satisfies readonly SupportedBrowserLocale[];

export interface BrowserLocaleOptions {
    supportedLocales?: readonly SupportedBrowserLocale[];
}

export interface BrowserOrLanguageLocaleOptions extends BrowserLocaleOptions {
    browserLocale?: string | null | undefined;
    fallbackLocale?: string;
}

export function provideBrowserLocaleId(fallbackLocale: string, options: BrowserLocaleOptions = {}): Provider {
    return {
        provide: LOCALE_ID,
        useFactory: () => resolveBrowserLocale(fallbackLocale, undefined, options)
    };
}

export function resolveBrowserLocale(
    fallbackLocale: string,
    browserLocale: string | null | undefined = getPrimaryBrowserLocale(),
    options: BrowserLocaleOptions = {}
): SupportedBrowserLocale {
    const supportedLocales = options.supportedLocales ?? SUPPORTED_BROWSER_LOCALES;
    const fallback =
        resolveSupportedLocale(fallbackLocale, 'en-US', supportedLocales) ?? supportedLocales[0] ?? 'en-US';

    if (!browserLocale) {
        return fallback;
    }

    return resolveSupportedLocale(browserLocale, fallback, supportedLocales) ?? fallback;
}

export function resolveBrowserOrLanguageLocale(
    selectedLanguage: string | null | undefined,
    options: BrowserOrLanguageLocaleOptions = {}
): SupportedBrowserLocale {
    const supportedLocales = options.supportedLocales ?? SUPPORTED_BROWSER_LOCALES;
    const fallbackLocale =
        resolveSupportedLocale(options.fallbackLocale ?? 'en-US', 'en-US', supportedLocales) ??
        supportedLocales[0] ??
        'en-US';
    const selectedLanguageLocale = selectedLanguage
        ? resolveSupportedLocale(selectedLanguage, fallbackLocale, supportedLocales)
        : null;

    return resolveBrowserLocale(selectedLanguageLocale ?? fallbackLocale, options.browserLocale, { supportedLocales });
}

function getPrimaryBrowserLocale(): string | undefined {
    if (typeof navigator === 'undefined') {
        return undefined;
    }

    const primaryLanguage = navigator.languages?.[0];
    return primaryLanguage || navigator.language || undefined;
}

function resolveSupportedLocale(
    locale: string,
    defaultEnglishLocale: SupportedBrowserLocale,
    supportedLocales: readonly SupportedBrowserLocale[]
): SupportedBrowserLocale | null {
    const canonicalLocale = canonicalizeLocale(locale);

    if (!canonicalLocale) {
        return null;
    }

    const [language, region] = canonicalLocale.split('-');

    switch (language.toLowerCase()) {
        case 'de':
            return getSupportedLocale('de-DE', supportedLocales);
        case 'nl':
            return getSupportedLocale('nl-NL', supportedLocales);
        case 'tr':
            return getSupportedLocale('tr-TR', supportedLocales);
        case 'en':
            if (region?.toUpperCase() === 'US') {
                return getSupportedLocale('en-US', supportedLocales);
            }

            if (
                !region &&
                defaultEnglishLocale.startsWith('en-') &&
                getSupportedLocale(defaultEnglishLocale, supportedLocales)
            ) {
                return defaultEnglishLocale;
            }

            return getSupportedLocale('en-GB', supportedLocales) ?? getSupportedLocale('en-US', supportedLocales);
        default:
            return null;
    }
}

function getSupportedLocale(
    locale: SupportedBrowserLocale,
    supportedLocales: readonly SupportedBrowserLocale[]
): SupportedBrowserLocale | null {
    return supportedLocales.includes(locale) ? locale : null;
}

function canonicalizeLocale(locale: string): string | null {
    const normalizedLocale = locale.trim().replace(/_/g, '-');

    if (!normalizedLocale) {
        return null;
    }

    if (typeof Intl === 'undefined' || typeof Intl.getCanonicalLocales !== 'function') {
        return normalizedLocale;
    }

    try {
        return Intl.getCanonicalLocales(normalizedLocale)[0] ?? null;
    } catch {
        return null;
    }
}
